
import { Component, Prop, Vue } from 'vue-property-decorator';
import { SettingItem, FileParameter, SettingsApiUiClient } from '../../backend/backendapi';

@Component
export default class SettingImageUploader extends Vue {
  @Prop({ type: Object, required: true }) readonly item!: SettingItem;
  @Prop({ type: Object, required: true }) readonly service!: SettingsApiUiClient;

  isUploading = false;
  error: string | null = null;
  filename: string | null = null;
  previewUrl: string | null = null;

  mounted(): void {
    if (this.item.valueMisc) {
      this.loadMedia();
    }
  }

  async loadMedia(): Promise<void> {
    try {
      const publicUrl = await this.service.getMedia(this.item.internalId);
      if (publicUrl) {
        this.previewUrl = publicUrl;
      }
    } catch (err) {
      console.error(`Failed to load image for ${this.item.internalId}:`, err);
    }
  }

  async handleFileSelect(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.error = 'Please select an image file';
      return;
    }

    const maxSize = 0.5 * 1024 * 1024; // 0.5 MB
    if (file.size > maxSize) {
      this.error = 'File size must be less than 0.5MB';
      return;
    }

    this.error = null;
    this.setUploadingState(true);

    // Clean up old local blob URL before assigning a new one to prevent memory leaks
    this.revokeLocalBlobUrl();

    const localPreviewUrl = URL.createObjectURL(file);
    this.previewUrl = localPreviewUrl;
    this.filename = file.name;

    const fileParams: FileParameter[] = [{ data: file, fileName: file.name }];

    try {
      const result = await this.service.uploadImage(
        this.item.internalId,
        null,
        null,
        fileParams
      );

      // Mutate item properties consistently
      this.item.value = result.mediaId;
      this.item.valueMisc = result.mediaId; // Keep value and valueMisc in sync
      this.filename = result.filename || file.name;

      this.$toasted.info(this.$t('txtDone').toString());
    } catch (err: any) {
      this.revokeLocalBlobUrl();
      this.previewUrl = null;
      this.error = err?.response?.data?.detail 
        || err?.message 
        || 'Failed to upload image. Please try again.';
    } finally {
      this.setUploadingState(false);
      if (input) input.value = '';
    }
  }

  async removeImage(): Promise<void> {
    const mediaId = this.item.valueMisc || this.item.value;

    this.revokeLocalBlobUrl();
    this.previewUrl = null;
    this.filename = null;
    this.error = null;

    this.item.value = '';
    this.item.valueMisc = '';

    if (mediaId && this.isValidGuid(mediaId)) {
      try {
        await this.service.deleteImage(this.item.internalId, mediaId);
        this.$toasted.info(this.$t('txtDone').toString());
      } catch (err) {
        this.$toasted.error(this.$t('txtError').toString());
      }
    }
  }

  handleImageError(): void {
    this.previewUrl = null;
  }

  private setUploadingState(status: boolean): void {
    this.isUploading = status;
    this.$emit('upload-status-change', status);
  }

  private isValidGuid(value: string): boolean {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(value);
  }

  private revokeLocalBlobUrl(): void {
    if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewUrl);
    }
  }

  beforeDestroy(): void {
    this.revokeLocalBlobUrl();
  }
}
